import React, { useMemo, useState } from "react"
import { Navigate, Outlet, useParams } from "react-router-dom"
import { Box, CssBaseline, ThemeProvider } from "@mui/material"
import { ConfigProvider, useConfigFetch } from "../hooks/useConfig"
import { BoothSessionProvider } from "../hooks/useBoothSession"
import { useParticipant } from "../hooks/useParticipant"
import { hasExpectedData } from "../library/Participant"
import { createMuiTheme } from "../library/MuiTheme"
import Loading from "./common/Loading"
import PageNotFound from "./views/PageNotFound"
import ClosedModal from "./modals/ClosedModal"
import AgeGateModal from "./modals/AgeGateModal"
import { ReactComponent as DefaultHeader } from "../icon/default-header.svg"

function BoothLayout() {
  const params = useParams<"code" | "step" | "uploadCode" | "picPresentation">()
  const code = params.code!

  const { config, error, loading } = useConfigFetch(code)
  const { participant } = useParticipant(config?.presetCode ?? code)

  const [checkedAge, setCheckedAge] = useState(() => {
    return sessionStorage.getItem("failedAgeGate") !== null
  })
  const [failedAgeGate, setFailedAgeGate] = useState(() => {
    return sessionStorage.getItem("failedAgeGate") === "true"
  })
  const muiTheme = useMemo(() => (config ? createMuiTheme(config.theme) : null), [config])

  const passAgeGate = (didPass: boolean) => {
    setCheckedAge(true)
    setFailedAgeGate(!didPass)

    sessionStorage.setItem("failedAgeGate", String(!didPass))
  }

  if (error) {
    return <PageNotFound error={error} />
  }

  if (loading || !config) {
    return (
      <Box sx={{my: "45vh", mx: "auto", textAlign: "center"}}>
        <Loading/>
      </Box>
    )
  }

  const lockOutForAge = config.ageGate.enabled && failedAgeGate
  const theme = config.theme
  const step = params.step ?? null

  // Redirect to splash if participant doesn't have required data (except for splash and pic pages)
  const requiresParticipant = step !== null && step !== "pic"
  if (requiresParticipant && !hasExpectedData(participant, config.dataCapture)) {
    return (
      <Navigate
        to={`/${config.presetCode}`}
        replace
      />
    )
  }

  const headerImage = (
    <Box sx={{ mt: 2, mb: 3 }}>
      {theme.headerImage && theme.headerImage.url ? (
        <img
          src={theme.headerImage.url}
          alt="Header graphic"
          style={{ maxWidth: "100%", maxHeight: "160px" }}
        />
      ) : (
        <DefaultHeader style={{ fill: theme.textColor, maxHeight: 120 }} />
      )}
    </Box>
  )

  return (
    <ThemeProvider theme={muiTheme!}>
      <CssBaseline/>
      <Box
        component="main"
        sx={{
          position: "relative",
          background: "none no-repeat center center fixed",
          backgroundSize: "cover",
          minHeight: "100vh",
          textAlign: "center",
          ...theme.backgroundStyle,
        }}
      >
        <Box
          sx={{
            display: "block",
            position: "absolute",
            inset: 0,
            ...theme.backgroundImageStyle,
          }}
        />
        <Box
          sx={{
            display: "block",
            position: "absolute",
            inset: 0,
            pb: "32px",
            overflowY: "auto",
          }}
        >
          {theme.headerImageUrl ? (
            <a
              href={theme.headerImageUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              {headerImage}
            </a>
          ) : (
            headerImage
          )}
          <ConfigProvider value={config}>
            <BoothSessionProvider>
              <Outlet />
            </BoothSessionProvider>
          </ConfigProvider>

          <ClosedModal
            show={config.closed || lockOutForAge}
            gallery={config.gallery}
          />
          <AgeGateModal
            prompt={config.ageGate.prompt}
            minimumAge={config.ageGate.minimumAge}
            show={config.ageGate.enabled && !checkedAge && step !== "pic"}
            passAgeGate={(didPass: boolean) => passAgeGate(didPass)}
          />
        </Box>
      </Box>
    </ThemeProvider>
  )
}

export default BoothLayout
